// path: src/components/ProfilePage/PersonalHistory/PersonalHistory.jsx
"use client";
import React from "react";
import { Spin, Typography } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useHistoryForm } from "../../../hooks/useHistoryForm";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import "./PersonalHistory.css";
const { Text } = Typography;

export default function PersonalHistory() {
  const { t } = useTranslation();

  const userId = useSelector(selectEffectiveUserId);

  const { lifestyle, allergies, surgeries, familyHistory, chronic, loading } = useHistoryForm(userId);
  const lifestyleRows = lifestyle
    .filter((row) => row.active)
    .map((row) => {
      const labels = {
        SMOKING: ["personalHistory.personal.items.smoking", "🚭"],
        ALCOHOL: ["personalHistory.personal.items.alcohol", "🍷"],
        SPORT: ["personalHistory.personal.items.physicalActivity", "🏃"],
      };
      const [labelKey, icon] = labels[row.type] || [null, "•"];
      const levelKeys = {
        daily: "daily",
        quotidian: "daily",
        quotidiennement: "daily",
        weekly: "weekly",
        hebdomadaire: "weekly",
        never: "never",
        jamais: "never",
        occasionally: "occasionally",
        occasionnel: "occasionally",
        occasionnellement: "occasionally",
      };
      const level = String(row.level || "").trim();
      const levelKey = levelKeys[level.toLowerCase()];

      return {
        id: row.id,
        type: labelKey ? t(labelKey) : row.label,
        value: levelKey ? t(`historyForm.levels.${levelKey}`) : level,
        status: level,
        icon,
      };
    });

  const chipClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "never") return "chip chip--success";
    if (s === "occasionally") return "chip chip--warn";
    if (s === "weekly") return "chip chip--warn";
    if (s === "daily") return "chip chip--danger";
    if (s === "normal") return "chip chip--success";
    if (s === "mild" || s === "mild reaction") return "chip chip--warn";
    if (s === "severe") return "chip chip--danger";
    if (s === "current" || s === "info") return "chip chip--info";
    if (s === "past") return "chip chip--warn";
    if (s === "moderate") return "chip chip--warn";
    if (s === "life_threatening" || s === "life-threatening") return "chip chip--danger";
    return "chip chip--muted";
  };

  const allergySeverity = (allergy) => {
    if (allergy.model === "surgicalHistory") {
      return allergy.reactionDescription || "";
    }

    const severityKeys = {
      MILD: "mild",
      MODERATE: "moderate",
      SEVERE: "severe",
      LIFE_THREATENING: "lifeThreatening",
    };
    const key = severityKeys[allergy.severityLevel];
    return key ? t(`personalHistory.allergies.severity.${key}`) : "";
  };

  const allergyCategory = (allergy) => {
    const categoryKeys = {
      DRUG: "drug",
      FOOD: "food",
      ENVIRONMENTAL: "environmental",
    };
    const key = categoryKeys[allergy.category];
    return key ? t(`personalHistory.allergies.categories.${key}`) : "";
  };

  if (loading) {
    return (
      <div className="ph-card ph-loading">
        <Spin tip={t("personalHistory.loading")} />
      </div>
    );
  }

  return (
    <div className="ph-card">
      {/* ===== HEADER ===== */}
      <div className="ph-head">
        <h2>{t("personalHistory.header.title")}</h2>
        <p>{t("personalHistory.header.subtitle")}</p>
      </div>

      {/* ===== PERSONAL ===== */}
      <section className="ph-section ph-section--grid">
        <div className="ph-col-head">
          <span className="ph-sec-ico" aria-hidden>
            👤
          </span>
          <span>{t("personalHistory.personal.title")}</span>
        </div>
        <div className="ph-col-body">
          {lifestyleRows.length === 0 ? (
            <div className="ph-row">
              <span className="ph-label muted">{t("personalHistory.personal.empty")}</span>
            </div>
          ) : (
            lifestyleRows.map((it) => (
              <div className="ph-row" key={it.id}>
                <div className="ph-left">
                  <span className="ph-ico" aria-hidden>
                    {it.icon}
                  </span>
                  <span className="ph-label">{it.type}</span>
                </div>
                <div className="ph-right">
                  <span className={chipClass(it.status)}>{it.value || "—"}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ===== ALLERGIES ===== */}
      <section className="ph-section ph-section--grid">
        <div className="ph-col-head">
          <span className="ph-sec-ico" aria-hidden>
            🤲
          </span>
          <span>{t("personalHistory.allergies.title")}</span>
        </div>
        <div className="ph-col-body">
          {!allergies || allergies.length === 0 ? (
            <div className="ph-row">
              <span className="ph-label muted">{t("personalHistory.allergies.empty")}</span>
            </div>
          ) : (
            allergies.map((al) => (
              <div className="ph-row" key={al.id}>
                <div className="ph-left ph-allergy-info">
                  <span className="ph-dot" />
                  <span className="ph-allergy-text">
                    <span className="ph-label">{al.name}</span>
                  </span>
                </div>
                <div className="ph-right">
                  {allergySeverity(al) && (
                    <span className={chipClass(
                      al.model === "surgicalHistory"
                        ? al.reactionDescription
                        : al.severityLevel,
                    )}>
                      {allergySeverity(al)}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ===== SURGICAL HISTORY ===== */}
      <section className="ph-section ph-section--grid">
        <div className="ph-col-head">
          <span className="ph-sec-ico" aria-hidden>
            🛠️
          </span>
          <span>{t("personalHistory.surgical.title")}</span>
        </div>
        <div className="ph-col-body-surgical">
          {!surgeries || surgeries.length === 0 ? (
            <div className="ph-row">
              <span className="ph-label muted">{t("personalHistory.surgical.empty")}</span>
            </div>
          ) : (
            surgeries.map((s) => (
              <div className="ph-row" key={s.id}>
                <div className="ph-left">
                  <span className="ph-dot" />
                  <span className="ph-label">
                    {s.type}
                    {s.year ? ` – ${s.year}` : ""}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ===== FAMILY HISTORY ===== */}
      <section className="ph-section ph-section--grid">
        <div className="ph-col-head">
          <span className="ph-sec-ico" aria-hidden>
            👨‍👩‍👧‍👦
          </span>
          <span>{t("personalHistory.family.title")}</span>
        </div>
        <div className="ph-col-body-family">
          {!familyHistory || familyHistory.length === 0 ? (
            <div className="ph-row">
              <span className="ph-label muted">{t("personalHistory.family.empty")}</span>
            </div>
          ) : (
            familyHistory.map((fh) => (
              <div className="ph-row" key={fh.id}>
                <div className="ph-left">
                  <span className="ph-dot" />
                  <span className="ph-label">
                    {fh.relation} – {fh.condition}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ===== CHRONIC CONDITIONS ===== */}
      <section className="ph-section ph-section--grid">
        <div className="ph-col-head">
          <span className="ph-sec-ico" aria-hidden>
            ♾️
          </span>
          <span>{t("personalHistory.chronic.title")}</span>
        </div>
        <div className="ph-col-body-chronic">
          {!chronic || chronic.length === 0 ? (
            <div className="ph-row">
              <span className="ph-label muted">{t("personalHistory.chronic.empty")}</span>
            </div>
          ) : (
            chronic.map((c) => (
              <div className="ph-row" key={c.id}>
                <div className="ph-left">
                  <span className="ph-dot" />
                  <span className="ph-label">
                    {c.condition}
                    {c.diagnosis ? ` – ${c.diagnosis}` : ""}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}